import React, { useContext, useEffect, useRef, useState } from 'react';
import './devices.css';
import AIEduDataTable from '../../components/DataTable/AIEduDataTable';
import AIEduSearch from '../../components/Search/AIEduSearch';
import AIEduPaginator from '../../components/Paginator/AIEduPaginator';
import AIEduButton from '../../components/Button/AIEduButton';
import AIEduSpinner from '../../components/Spinner/AIEduSpinner';
import CustomSpinner from '../../components/Spinner/CustomSpinner';
import AIEduToast from '../../components/Toast/AIEduToast';
import AIEduDialog from '../../components/Dialog/AIEduDialog';
import AddDevice from './AddDevice';
import { API } from '../../services';
import { useAuthenticate } from '../../hooks';
import { Context } from '../../context/Context';
import {
    setToastComponent,
    showSuccessToast,
    showErrorToast,
} from '../../services/setup/ApiResponseHandler';
import DeleteDeviceDialog from './DeleteDeviceDialog';
import { useIntl } from 'react-intl';
import { formatDateWithTimeStamp } from '../../utils/Utils';
import { ASC, DESC } from '../../utils/Constants';
import PageTitle from '../../layout/PageTitle/pageTitle';

const Devices = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {}; // assuming there is only one organization for now. This can be changed later.

    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const {
        refreshMap,
        isAddDeviceVisible,
        setIsAddDeviceVisible,
        sideMenuCurrentActive,
    } = useContext(Context);

    const [searchTerm, setSearchTerm] = useState('');
    const toastRef = useRef(null);
    const [selectedDevicesList, setSelectedDevicesList] = useState([]);
    const [visible, setVisible] = useState(false);
    const [deleteAction, setDeleteAction] = useState(null);
    const [deleteMessage, setDeleteMessage] = useState('');
    const [dialogTitle, setDialogTitle] = useState('');
    const [actionLabel, setActionLabel] = useState('');
    const [actionClass, setActionClass] = useState('');

    const [isAssignTagDialogVisible, setIsAssignTagDialogVisible] =
        useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [selectedTag, setSelectedTag] = useState(null);
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const [tagsPage, setTagsPage] = useState(0);
    const [tagsRowsPerPage] = useState(5);
    const [hasMoreTags, setHasMoreTags] = useState(true);
    const [isLoadingMoreTags, setIsLoadingMoreTags] = useState(false);

    const [sortField, setSortField] = useState('createdTime');
    const [sortOrder, setSortOrder] = useState(-1);
    const [tableKey, setTableKey] = useState(false);
    const tagsContainerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const observerRef = useRef(null);

    const onPageChange = (e) => {
        const page = e.page;
        const size = e.rows;

        setFirst(e.first);
        setRows(size);
        setPage(page);
        setTableKey(!tableKey);
        setSelectedDevicesList([]);

        getAllScreenDetails(page, size, sortField, sortOrder, searchTerm);
    };

    const getAllScreenDetails = (start, rowsPerPage, sortBy, sort, search) => {
        setLoading(true);
        const params = {
            start,
            rowsPerPage,
            sortBy: sortBy || 'createdTime',
            sort: sort === 1 ? ASC : DESC,
            search,
        };
        API.getScreensDetails(organizationId, placeId, params)
            .then(({ data }) => {
                const { screens, total, sortBy, sort } = data ?? {};
                if (screens?.length === 0 && start > 0 && total > 0) {
                    setFirst(0);
                    setPage(0);
                    getAllScreenDetails(
                        0,
                        rowsPerPage,
                        sortBy,
                        sortOrder,
                        search
                    );
                } else {
                    const devicesList = [];
                    screens?.forEach((screen) => {
                        const { screenId, deviceId, screenName, createdTime, tags } =
                            screen ?? {};
                        const { tagName } = tags?.[0] ?? {};
                        devicesList.push({
                            screenId,
                            deviceId,
                            createdTime,
                            screenName,
                            tag: tagName,
                        });
                    });
                    setDevices(devicesList);
                    setTotalRecords(total);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const resetPageParams = () => {
        setFirst(0);
        setPage(0);
        setSortField('createdTime');
        setSortOrder(-1);
        setSearchTerm('');
        setSelectedDevicesList([]);
    };

    useEffect(() => {
        setToastComponent(toastRef.current);
    }, []);

    useEffect(() => {
        if (sideMenuCurrentActive !== 0) {
            setIsAddDeviceVisible(false);
        }

        resetPageParams();
        getAllScreenDetails(0, rows);
    }, [refreshMap.device]);

    // IntersectionObserver for infinite scroll
    useEffect(() => {
        if (!isAssignTagDialogVisible || !hasMoreTags || isLoadingMoreTags) {
            // Clean up observer when dialog is closed or no more tags
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            return;
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    loadMoreTags();
                }
            },
            {
                root: tagsContainerRef.current,
                rootMargin: '0px',
                threshold: 0.1,
            }
        );

        if (loadMoreRef.current) {
            observerRef.current.observe(loadMoreRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [
        isAssignTagDialogVisible,
        hasMoreTags,
        isLoadingMoreTags,
        tagsPage,
        availableTags.length,
    ]);

    const actionBodyTemplate = (rowData) => {
        const { screenId } = rowData ?? {};
        const confirmSingleDelete = () => {
            setDeleteMessage(f({ id: 'AI_EDU_DELETE_DEVICE_TEXT' }));
            setDialogTitle(f({ id: 'AI_EDU_DELETE_SCREEN' }));
            setActionLabel(f({ id: 'AI_EDU_DELETE' }));
            setActionClass('btnDelete');
            setDeleteAction(() => () => {
                handleDeleteDevicesList([screenId]);
            });
            setVisible(true);
        };
        return (
            <i
                className='pi pi-trash cursor-pointer'
                style={{ fontSize: '20px' }}
                onClick={confirmSingleDelete}
            />
        );
    };

    const createdTimeTemplate = (rowData) => {
        return !!rowData.createdTime
            ? formatDateWithTimeStamp(rowData.createdTime)
            : '--';
    };

    const deviceTagsTemplate = (rowData) => {
        return !!rowData.tag ? rowData.tag : '--';
    };

    const serialNumberTemplate = (rowData) => {
        return !!rowData.deviceId ? rowData.deviceId : '--';
    }

    const deviceNameTemplate = (rowData) => {
        return !!rowData.screenName ? rowData.screenName : '--';
    }

    const columns = [
        {
            field: 'screenId',
            header: f({ id: 'AI_EDU_DEVICE_MAC' }),
            width: '15%',
            minWidth: '10rem',
            sortable: true,
        },
        {
            field: 'deviceId',
            header: f({ id: 'AI_EDU_DEVICE_SERIAL_NUMBER' }),
            body: serialNumberTemplate,
            width: '20%',
            minWidth: '15rem',
            sortable: true,
        },
        {
            field: 'screenName',
            header: f({ id: 'AI_EDU_DEVICE_NAME' }),
            body: deviceNameTemplate,
            width: '20%',
            minWidth: '15rem',
            sortable: true,
        },
        {
            field: 'tag',
            header: f({ id: 'AI_EDU_DEVICE_TAG' }),
            width: '20%',
            minWidth: '15rem',
            body: deviceTagsTemplate,
        },
        {
            header: f({ id: 'AI_EDU_ENROLLED_DATE' }),
            field: 'createdTime',
            width: '15%',
            minWidth: '10rem',
            body: createdTimeTemplate,
            sortable: true,
        },
        {
            header: f({ id: 'AI_EDU_ACTION' }),
            body: actionBodyTemplate,
            minWidth: '5rem',
        },
    ];

    const handleDeleteDevicesList = (deviceList) => {
        API.deleteScreens(organizationId, placeId, deviceList)
            .then((res) => {
                if (res.status === 200) {
                    setSelectedDevicesList((prev) =>
                        prev.filter(
                            (device) => !deviceList.includes(device.screenId)
                        )
                    );
                    if (deviceList === devices) {
                        setPage(Math.max(0, page - 1));
                    }
                    getAllScreenDetails(
                        deviceList === devices ? Math.max(0, page - 1) : page,
                        rows,
                        sortField,
                        sortOrder,
                        searchTerm
                    );
                }
            })
            .finally(() => {
                setVisible(false);
                setDeleteMessage('');
                setDialogTitle('');
                setActionLabel('');
                setActionClass('');
            });
    };

    const navigateToAddDevice = () => {
        setIsAddDeviceVisible(true);
    };

    const handleMultipleDeviceDelete = () => {
        setDeleteMessage(f({ id: 'AI_EDU_DELETE_DEVICE_TEXT' }));
        setDialogTitle(f({ id: 'AI_EDU_DELETE_SCREEN' }));
        setActionLabel(f({ id: 'AI_EDU_DELETE' }));
        setActionClass('btnDelete');
        const screens = selectedDevicesList?.map((devices) => devices.screenId);
        setDeleteAction(() => () => handleDeleteDevicesList(screens));
        setVisible(true);
    };

    const handleAssignTag = () => {
        if (selectedDevicesList.length === 0) {
            showErrorToast(
                f({ id: 'AI_EDU_SELECT_AT_LEAST_ONE_DEVICE_TO_ASSIGN_TAG' })
            );
            return;
        }
        setIsAssignTagDialogVisible(true);
        loadAvailableTags();
    };

    const handleRemoveTag = () => {
        setDeleteMessage(f({ id: 'AI_EDU_REMOVE_TAG_CONFIRMATION' }));
        setDialogTitle(f({ id: 'AI_EDU_REMOVE_TAG_TITLE' }));
        setActionLabel(f({ id: 'AI_EDU_REMOVE_TAG' }));
        setActionClass('btnDelete');

        const removeTagsFromDevices = async () => {
            const deviceIds = selectedDevicesList.map(
                (device) => device.screenId
            );

            try {
                const res = await API.removeTagFromScreens(
                    organizationId,
                    placeId,
                    deviceIds
                );
                if (res.status === 200) {
                    const { data } = res ?? {};
                    const { message } = data ?? {};
                    showSuccessToast(message);
                    setSelectedDevicesList([]);
                    // Refresh the device list
                    getAllScreenDetails(
                        page,
                        rows,
                        sortField,
                        sortOrder,
                        searchTerm
                    );
                } else {
                    showErrorToast(
                        f({ id: 'AI_EDU_FAILED_TO_REMOVE_TAG_FROM_DEVICES' })
                    );
                }
            } catch (error) {
                showErrorToast(
                    f({ id: 'AI_EDU_FAILED_TO_REMOVE_TAG_FROM_DEVICES' })
                );
            } finally {
                setVisible(false);
                setDeleteMessage('');
                setDialogTitle('');
                setActionLabel('');
                setActionClass('');
            }
        };

        setDeleteAction(() => removeTagsFromDevices);
        setVisible(true);
    };

    const loadAvailableTags = () => {
        setIsLoadingTags(true);
        setTagsPage(0);
        setHasMoreTags(true);
        API.getAllTags(organizationId, placeId, {
            start: 0,
            rowsPerPage: tagsRowsPerPage,
        })
            .then(({ data }) => {
                const { data: tagsData, total } = data ?? {};
                const { tags } = tagsData ?? {};
                const tagsList = tags || [];
                const totalTags = parseInt(total || 0);
                setAvailableTags(tagsList);
                const hasMore =
                    tagsRowsPerPage < totalTags && tagsList.length > 0;
                setHasMoreTags(hasMore);
            })
            .finally(() => {
                setIsLoadingTags(false);
            });
    };

    const loadMoreTags = () => {
        if (isLoadingMoreTags || !hasMoreTags) return;

        setIsLoadingMoreTags(true);
        const nextPage = tagsPage + 1;
        const start = nextPage;

        API.getAllTags(organizationId, placeId, {
            start,
            rowsPerPage: tagsRowsPerPage,
        })
            .then(({ data }) => {
                const { data: tagsData, total } = data ?? {};
                const { tags } = tagsData ?? {};
                const newTags = tags || [];
                const totalTags = parseInt(total || 0);

                setAvailableTags((prev) => {
                    const updatedTags = [...prev, ...newTags];
                    // Only load more if we haven't reached the total count
                    const hasMore =
                        (start + 1) * tagsRowsPerPage < totalTags &&
                        newTags.length > 0;
                    setHasMoreTags(hasMore);
                    return updatedTags;
                });
                setTagsPage(nextPage);
            })
            .catch((error) => {
                showErrorToast('Failed to load more tags');
            })
            .finally(() => {
                setIsLoadingMoreTags(false);
            });
    };

    const handleTagAssignment = () => {
        if (!selectedTag) {
            showErrorToast(f({ id: 'AI_EDU_SELECT_TAG_TO_ASSIGN' }));
            return;
        }

        const assignTagsToDevices = async () => {
            const deviceIds = selectedDevicesList.map(
                (device) => device.screenId
            );

            try {
                const res = await API.assignTagToScreens(
                    organizationId,
                    placeId,
                    deviceIds,
                    selectedTag.tagId
                );
                if (res.status === 201) {
                    showSuccessToast(
                        f({ id: 'AI_EDU_TAG_ASSIGNED_SUCCESSFULLY' }, { count: deviceIds.length })
                    );
                    setIsAssignTagDialogVisible(false);
                    setSelectedTag(null);
                    setSelectedDevicesList([]);
                    // Refresh the device list if needed
                    getAllScreenDetails(
                        page,
                        rows,
                        sortField,
                        sortOrder,
                        searchTerm
                    );
                } else {
                    showErrorToast(
                        f({ id: 'AI_EDU_FAILED_TO_ASSIGN_TAG_TO_DEVICES' })
                    );
                }
            } catch (error) {
                showErrorToast(
                    f({ id: 'AI_EDU_FAILED_TO_ASSIGN_TAG_TO_DEVICES' })
                );
            }
        };

        assignTagsToDevices();
    };

    const closeAssignTagDialog = () => {
        setIsAssignTagDialogVisible(false);
        setSelectedTag(null);
        setAvailableTags([]);
        setTagsPage(0);
        setHasMoreTags(true);
        setIsLoadingMoreTags(false);
    };

    const handleDeviceSearch = () => {
        setFirst(0);
        setPage(0);
        setSelectedDevicesList([]);
        getAllScreenDetails(0, rows, sortField, sortOrder, searchTerm);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleDeviceSearch();
        }
    };

    const onSort = (e) => {
        const { field, order } = { field: e.sortField, order: e.sortOrder };

        getAllScreenDetails(
            page,
            rows,
            field,
            order,
            !!searchTerm ? searchTerm : null
        );

        setSortField(field);
        setSortOrder(order);
    };

    const sortIcon = (options) => {
        if (options.sortOrder === 1) {
            return (
                <i
                    className='pi pi-sort-up-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
        if (options.sortOrder === -1) {
            return (
                <i
                    className='pi pi-sort-down-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
    };

    return (
        <>
            {loading && <AIEduSpinner />}
            <DeleteDeviceDialog
                visible={visible}
                hideDeleteDevicesDialog={() => setVisible(!visible)}
                deleteSelectedDevices={deleteAction}
                deleteMessage={deleteMessage}
                dialogTitle={dialogTitle}
                actionLabel={actionLabel}
                actionClass={actionClass}
            />
            <AIEduDialog
                visible={isAssignTagDialogVisible}
                draggable={false}
                style={{ width: '40vw', minWidth: '35rem' }}
                title={f({ id: 'AI_EDU_ASSIGN_TAG_TO_DEVICES' })}
                content={
                    <div>
                        {isLoadingTags ? (
                            <div className='flex justify-content-center'>
                                <CustomSpinner />
                            </div>
                        ) : availableTags.length === 0 ? (
                            <div className='text-center text-500'>
                                {f({
                                    id: 'AI_EDU_NO_TAGS_AVAILABLE_CREATE_FIRST',
                                })}
                            </div>
                        ) : (
                            <div
                                ref={tagsContainerRef}
                                className='device-tags-container flex flex-column gap-2 p-2'
                            >
                                {availableTags.map((tag) => (
                                    <div
                                        key={tag.tagId}
                                        className={`p-2 border-1 border-round cursor-pointer ${
                                            selectedTag?.tagId === tag.tagId
                                                ? 'border-primary bg-primary-50'
                                                : 'border-300 hover:border-400'
                                        }`}
                                        onClick={() => {
                                            // Only allow one tag to be selected
                                            if (
                                                selectedTag?.tagId === tag.tagId
                                            ) {
                                                setSelectedTag(null); // Deselect if already selected
                                            } else {
                                                setSelectedTag(tag); // Select new tag (replaces any previous selection)
                                            }
                                        }}
                                    >
                                        <div className='flex align-items-center gap-2'>
                                            <div className='font-medium'>
                                                {tag.tagName}
                                            </div>
                                            {selectedTag?.tagId ===
                                                tag.tagId && (
                                                <i className='pi pi-check text-primary' />
                                            )}
                                        </div>
                                        {tag.description && (
                                            <div className='text-sm text-600'>
                                                {tag.description}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {isLoadingMoreTags && (
                                    <div className='flex justify-content-center p-3'>
                                        <CustomSpinner />
                                    </div>
                                )}
                                {hasMoreTags && !isLoadingMoreTags && (
                                    <div
                                        ref={loadMoreRef}
                                        className='load-more-tags-trigger'
                                    >
                                        {f({ id: 'AI_EDU_LOAD_MORE' })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                }
                footer={
                    <div className='flex justify-content-end'>
                        <AIEduButton
                            label={f({ id: 'AI_EDU_CANCEL' })}
                            className='btnSecondary'
                            onClick={closeAssignTagDialog}
                        />
                        <AIEduButton
                            label={f({ id: 'AI_EDU_OK' })}
                            className='btnPrimary'
                            onClick={handleTagAssignment}
                            disabled={!selectedTag || isLoadingTags}
                        />
                    </div>
                }
                onHide={closeAssignTagDialog}
            />
            <AIEduToast ref={toastRef} />
            {!isAddDeviceVisible ? (
                <div className='devices'>
                    <PageTitle
                        title={f({ id: 'AI_EDU_DEVICES' })}
                        count={totalRecords}
                    />
                    <div className='mb-4 flex justify-content-between flex-wrap gap-3'>
                        <AIEduSearch
                            value={searchTerm}
                            setValue={setSearchTerm}
                            handleInputKeyDown={handleInputKeyDown}
                            placeholder={f({ id: 'AI_EDU_SEARCH_DEVICE_MAC' })}
                            searchHandler={handleDeviceSearch}
                        />
                        <div className='flex align-items-center'>
                            <AIEduButton
                                icon='pi pi-trash'
                                className='devices-delete'
                                onClick={handleMultipleDeviceDelete}
                                disabled={selectedDevicesList.length === 0}
                            />
                            <div className='vertical_divider mx-3' />
                            <AIEduButton
                                label={f({ id: 'AI_EDU_REMOVE_TAG' })}
                                className='btnDelete mr-2'
                                onClick={handleRemoveTag}
                                disabled={selectedDevicesList.length === 0}
                            />
                            <AIEduButton
                                label={f({ id: 'AI_EDU_ASSIGN_TAG' })}
                                className='btnPrimary'
                                onClick={handleAssignTag}
                                disabled={selectedDevicesList.length === 0}
                            />
                            <div className='vertical_divider mx-3' />
                            <AIEduButton
                                label={f({ id: 'AI_EDU_ADD_DEVICE' })}
                                icon='pi pi-plus'
                                className='btnPrimary'
                                onClick={navigateToAddDevice}
                            />
                        </div>
                    </div>
                    <div
                        className='mb-2'
                        style={{
                            height: '1rem',
                            fontFamily: 'SamsungOne-700, sans-serif',
                        }}
                    >
                        {selectedDevicesList?.length > 0 && (
                            <span>
                                {f({ id: 'AI_EDU_SELECTED' }, { 
                                        selected: selectedDevicesList.length,
                                    total: devices?.length 
                                })}
                            </span>
                        )}
                    </div>
                    <AIEduDataTable
                        key={tableKey}
                        data={devices}
                        columns={columns}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        sortIcon={sortIcon}
                        onSort={onSort}
                        lazy={true}
                        dataKey='screenId'
                        selection={selectedDevicesList}
                        onSelectionChange={(e) =>
                            setSelectedDevicesList(e.value)
                        }
                        scrollHeight='60vh'
                        selectionMode='multiple'
                        emptyMessage={f({ id: 'AI_EDU_NO_DATA_TO_SHOW' })}
                    />

                    {devices?.length > 0 && (
                        <AIEduPaginator
                            rows={rows}
                            first={first}
                            totalRecords={totalRecords}
                            onPageChange={onPageChange}
                        />
                    )}
                </div>
            ) : (
                <AddDevice
                    setIsAddDeviceVisible={setIsAddDeviceVisible}
                    setLoading={setLoading}
                />
            )}
        </>
    );
};

export default Devices;
